import { Alert, AlertTitle } from "@ui/components/alert";
import { CircleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";

/** Form-level error under the heading. Field errors stay on their fields. */
export function AuthError({ message }: { message: string | null }) {
	if (!message) {
		return null;
	}
	return (
		<Alert variant="destructive" role="alert">
			<CircleAlertIcon />
			<AlertTitle className="font-normal">{message}</AlertTitle>
		</Alert>
	);
}

/** Replaces a form once it has done its job ("check your inbox"). */
export function AuthDone({
	icon,
	title,
	children,
	actions,
}: {
	icon: ReactNode;
	title: ReactNode;
	children?: ReactNode;
	actions?: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-6">
			<span className="grid size-11 place-items-center rounded-2xl bg-brand/12 text-brand [&_svg]:size-5">
				{icon}
			</span>
			<div className="flex flex-col gap-2">
				<h1 className="text-balance font-semibold text-2xl tracking-tight">
					{title}
				</h1>
				{children ? (
					<div className="text-pretty text-muted-foreground text-sm">
						{children}
					</div>
				) : null}
			</div>
			{actions ? (
				<div className="flex flex-col gap-2">{actions}</div>
			) : null}
		</div>
	);
}

export function authErrorInput(error: unknown) {
	return error && typeof error === "object"
		? (error as { code?: string; message?: string })
		: null;
}

type FieldLike = {
	state: {
		value: unknown;
		meta: { isTouched: boolean; errors: unknown[] };
	};
};

/**
 * The one error to show under a field. Stays quiet while a field is empty and
 * untouched by a submit (clicking past an empty field isn't a mistake), and
 * shows only the first rule that fails instead of the whole list.
 *
 * `attempted` comes from the form's own subscription
 * (`useStore(form.store, (s) => s.submissionAttempts > 0)`): a first submit of an empty form
 * is refused before anything else in the form changes, so reading the count here, without
 * subscribing, left that first submit with no message at all.
 */
export function visibleFieldError(field: FieldLike, attempted: boolean) {
	const { value, meta } = field.state;
	const [first] = meta.errors;
	const typed = typeof value === "string" ? value.length > 0 : true;
	if (!first || !meta.isTouched || !(typed || attempted)) {
		return undefined;
	}
	const message =
		typeof first === "string"
			? first
			: ((first as { message?: string }).message ?? "Check this field");
	return [{ message }];
}
